import {Box, Card, Container, Flex, Group, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {useMemo, useState} from "react";
import {extractErrorMessage, tagDefinitionsSelector, useGetAllTagDefinitionsQuery} from "@ots-frontend/ots-data";
import {skipToken} from "@reduxjs/toolkit/query";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {TagDefinitionDataTable} from "../../components/data-table/TagDefinitionDataTable/TagDefinitionDataTable";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {useNavigate} from "react-router-dom";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";

export type TagDefinitionPageProps = {}

export const TagDefinitionDataTablePageRoute = () => {
  return (
    <TagDefinitionDataTablePage/>
  );
};

export const TagDefinitionDataTablePage = (props: TagDefinitionPageProps) => {

  const {t} = useTranslation(["translation", "common"]);
  const navigate = useNavigate()

  const [access, setAccess] = useState(false);

  const selectTagDefinitions = useMemo(() => {
    return tagDefinitionsSelector();
  }, []);

  const {
    tagDefinitions,
    isLoading,
    error,
    refetch,
  } = useGetAllTagDefinitionsQuery(access ? undefined : skipToken, {
    selectFromResult: (result) => ({
      ...result,
      tagDefinitions: selectTagDefinitions(result),
      isLoading: result.isLoading,
      error: result.isError ? extractErrorMessage(result.error) : undefined,
    })
  });

  return (
    <ProtectedPage
      permissions={["tagdefinition_view"]}
      isLoading={isLoading}
      onAccess={setAccess}
    >
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Flex direction="column" h="100%" m="sm" gap="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title order={4}>
                      {t("common.entity.tagdefinition.plural", "Tag Definitions", {ns: "common"})}
                    </Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.actions.plural", "Actions", {ns: "common"})}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("common.actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                        <HasAccess permissions={["tagdefinition_create"]}>
                          <>
                            <Menu.Divider/>
                            <Menu.Item onClick={() => navigate("/tags/definitions/form")}>
                              {t("common.action.new", "New", {ns: "common"})}
                            </Menu.Item>
                          </>
                        </HasAccess>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                {error &&
                  <Text size="xs" color="red" fw="bold">
                    {error}
                  </Text>
                }
                <Box sx={{flex: 1, overflow: 'auto'}}>
                  <TagDefinitionDataTable
                    withBorder
                    records={tagDefinitions}
                    recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                    fetching={isLoading}/>
                </Box>
              </Flex>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ProtectedPage>
  );
};
